/**
 * TopBar Component
 * 
 * Main application header displaying:
 * - Real-time ledger balance
 * - Notification bell with unread count
 * - Quick action button for new payments
 * - User profile dropdown
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Plus, 
  ChevronDown, 
  LogOut, 
  Settings, 
  User,
  Building2,
} from 'lucide-react';
import { cn, formatCurrency, formatRelativeTime, getInitials } from '../../lib/utils';
import type { Customer, Notification } from '../../types';

export interface TopBarProps {
  /** Current customer/business data */
  customer: Customer;
  /** Unread notification count */
  unreadCount?: number;
  /** Notifications list */
  notifications?: Notification[];
  /** Ledger balance in cents */
  ledgerBalanceCents?: number;
  /** WebSocket connection status */
  connectionStatus?: 'connected' | 'disconnected' | 'connecting';
  /** Callback when new payment button is clicked */
  onNewPayment?: () => void;
  /** Callback when notification is clicked */
  onNotificationClick?: (notification: Notification) => void;
  /** Callback when logout is clicked */
  onLogout?: () => void;
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * TopBar - Application header with balance, notifications, and user menu
 * 
 * @example
 * <TopBar 
 *   customer={customer}
 *   ledgerBalanceCents={150000}
 *   unreadCount={3}
 *   connectionStatus="connected"
 * />
 */
export const TopBar: React.FC<TopBarProps> = ({
  customer,
  unreadCount = 0,
  notifications = [],
  ledgerBalanceCents,
  connectionStatus = 'connected',
  onNewPayment,
  onNotificationClick,
  onLogout,
  className,
}) => {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [balance, setBalance] = useState(ledgerBalanceCents ?? customer.ledgerBalanceCents);
  const [balanceUpdated, setBalanceUpdated] = useState(false);
  
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // Update balance when prop changes with animation
  useEffect(() => {
    if (ledgerBalanceCents !== undefined && ledgerBalanceCents !== balance) {
      setBalance(ledgerBalanceCents);
      setBalanceUpdated(true);
      const timer = setTimeout(() => setBalanceUpdated(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [ledgerBalanceCents]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNewPayment = () => {
    if (onNewPayment) {
      onNewPayment();
    } else {
      navigate('/payments/new');
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500';
      case 'disconnected':
        return 'bg-red-500';
    }
  };

  return (
    <header className={cn(
      'h-16 bg-white border-b border-gray-200 px-4 lg:px-6 flex items-center justify-between sticky top-0 z-40',
      className
    )}>
      {/* Left section - Balance display */}
      <div className="flex items-center space-x-4">
        <div className="hidden md:flex flex-col">
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            Available Balance
          </span>
          <div className="flex items-center space-x-2">
            <span 
              className={cn(
                'text-xl font-bold text-gray-900 transition-all duration-300',
                balanceUpdated && 'text-green-600 scale-105'
              )}
            >
              {formatCurrency(balance)}
            </span>
            {/* Connection status indicator */}
            <div className="relative group">
              <div className={cn('w-2 h-2 rounded-full', getConnectionStatusColor())} />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {connectionStatus === 'connected' ? 'Live updates' : connectionStatus}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile balance (simplified) */}
        <div className="md:hidden">
          <span className="text-lg font-bold text-gray-900">
            {formatCurrency(balance)}
          </span>
        </div>
      </div>

      {/* Right section - Actions and user menu */}
      <div className="flex items-center space-x-3 lg:space-x-4">
        {/* New Payment Button */}
        <button
          onClick={handleNewPayment}
          className="inline-flex items-center px-3 lg:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          <span className="hidden sm:inline">New Payment</span>
          <span className="sm:hidden">New</span>
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={`Notifications (${unreadCount} unread)`}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="text-xs text-blue-600 font-medium">
                    {unreadCount} new
                  </span>
                )}
              </div>
              
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => {
                        onNotificationClick?.(notification);
                        setShowNotifications(false);
                      }}
                      className={cn(
                        'w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0',
                        !notification.read && 'bg-blue-50/50'
                      )}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={cn(
                          'w-2 h-2 mt-2 rounded-full flex-shrink-0',
                          !notification.read ? 'bg-blue-500' : 'bg-transparent'
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatRelativeTime(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
              
              {notifications.length > 0 && (
                <div className="px-4 py-2 border-t border-gray-100">
                  <Link
                    to="/notifications"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    onClick={() => setShowNotifications(false)}
                  >
                    View all notifications
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Profile */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setShowProfile(!showProfile)}
            className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
              {getInitials(customer.businessName)}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                {customer.businessName}
              </p>
              <p className="text-xs text-gray-500">
                {customer.onboardingStatus === 'approved' ? 'Verified' : 'Pending'}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
          </button>

          {/* Profile dropdown */}
          {showProfile && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-100 sm:hidden">
                <p className="font-medium text-gray-900">{customer.businessName}</p>
                <p className="text-sm text-gray-500">{formatCurrency(balance)}</p>
              </div>
              
              <Link
                to="/settings"
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setShowProfile(false)}
              >
                <Building2 className="w-4 h-4 mr-3 text-gray-400" />
                Business Settings
              </Link>
              
              <Link
                to="/settings/profile"
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setShowProfile(false)}
              >
                <User className="w-4 h-4 mr-3 text-gray-400" />
                Profile
              </Link>
              
              <Link
                to="/settings"
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setShowProfile(false)}
              >
                <Settings className="w-4 h-4 mr-3 text-gray-400" />
                Account Settings
              </Link>
              
              <hr className="my-2 border-gray-100" />
              
              <button
                onClick={() => {
                  onLogout?.();
                  setShowProfile(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-3" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
